import { useEffect, useMemo } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/page-header";
import { MobileHomeMenu } from "@/components/mobile-home-menu";
import {
  Users, GraduationCap, Megaphone, ClipboardCheck, Images, CalendarDays,
  Wallet, Clock, CheckCircle2, Ban,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid,
} from "recharts";
import { useIsMobile } from "@/hooks/use-mobile";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({ meta: [{ title: "Dashboard — MI Al-Munajah Admin" }] }),
  component: Dashboard,
});

type Stats = {
  students: number; teachers: number; announcements: number;
  attendanceMonth: number; schedules: number; gallery: number;
  payToday: number; payPending: number; payPaid: number; payRejected: number;
};

function Dashboard() {
  const isMobile = useIsMobile();
  const [stats, setStats] = useCachedState<Stats>("dashboard:stats", {
    students: 0, teachers: 0, announcements: 0, attendanceMonth: 0, schedules: 0, gallery: 0,
    payToday: 0, payPending: 0, payPaid: 0, payRejected: 0,
  });
  const [attendanceData, setAttendanceData] = useCachedState<{ name: string; value: number; color: string }[]>("dashboard:attendance", []);
  const [annByCat, setAnnByCat] = useCachedState<{ name: string; value: number }[]>("dashboard:annByCat", []);
  const [attTrend, setAttTrend] = useCachedState<{ date: string; hadir: number; absen: number }[]>("dashboard:attTrend", []);

  const load = async () => {
    const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0,0,0,0);
    const iso = monthStart.toISOString().slice(0, 10);

    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    const todayIso = todayStart.toISOString();

    const [students, teachers, anns, attMonth, schedules, gallery, attRows, annAll, payAll] = await Promise.all([
      supabase.from("students").select("*", { count: "exact", head: true }),
      supabase.from("teachers").select("*", { count: "exact", head: true }),
      supabase.from("announcements").select("*", { count: "exact", head: true }),
      supabase.from("attendance").select("*", { count: "exact", head: true }).gte("date", iso),
      supabase.from("schedules").select("*", { count: "exact", head: true }),
      supabase.from("gallery").select("*", { count: "exact", head: true }),
      supabase.from("attendance").select("date,status").gte("date", iso),
      supabase.from("announcements").select("category"),
      supabase.from("payments" as never).select("status,amount,paid_at,payment_date"),
    ]);

    const payRows = (payAll.data as Array<{ status: string; amount: number; paid_at: string | null; payment_date: string }> | null) ?? [];
    const payToday = payRows
      .filter((r) => r.status === "paid" && new Date(r.paid_at ?? r.payment_date) >= todayStart && new Date(r.paid_at ?? r.payment_date).toISOString() <= new Date().toISOString())
      .reduce((a, r) => a + Number(r.amount || 0), 0);
    void todayIso;

    setStats({
      students: students.count ?? 0,
      teachers: teachers.count ?? 0,
      announcements: anns.count ?? 0,
      attendanceMonth: attMonth.count ?? 0,
      schedules: schedules.count ?? 0,
      gallery: gallery.count ?? 0,
      payToday,
      payPending: payRows.filter((r) => r.status === "pending" || r.status === "waiting_verification").length,
      payPaid: payRows.filter((r) => r.status === "paid").length,
      payRejected: payRows.filter((r) => r.status === "rejected").length,
    });

    const counts = { hadir: 0, izin: 0, sakit: 0, alpha: 0 };
    (attRows.data || []).forEach((r: { status: keyof typeof counts }) => { counts[r.status] = (counts[r.status] || 0) + 1; });
    setAttendanceData([
      { name: "Hadir", value: counts.hadir, color: "var(--color-chart-1)" },
      { name: "Izin", value: counts.izin, color: "var(--color-chart-3)" },
      { name: "Sakit", value: counts.sakit, color: "var(--color-chart-4)" },
      { name: "Alpha", value: counts.alpha, color: "var(--color-chart-5)" },
    ]);

    const acc: Record<string, number> = { sekolah: 0, akademik: 0, kegiatan: 0 };
    const annRows = (annAll.data || []) as Array<{ category: string }>;
    for (const r of annRows) {
      acc[r.category] = (acc[r.category] || 0) + 1;
    }
    setAnnByCat(Object.entries(acc).map(([name, value]) => ({ name: name[0].toUpperCase() + name.slice(1), value })));

    // 7-day attendance trend
    const trendMap: Record<string, { hadir: number; absen: number }> = {};
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      trendMap[d.toISOString().slice(0, 10)] = { hadir: 0, absen: 0 };
    }
    (attRows.data || []).forEach((r: { date: string; status: string }) => {
      if (trendMap[r.date]) {
        if (r.status === "hadir") trendMap[r.date].hadir++;
        else trendMap[r.date].absen++;
      }
    });
    setAttTrend(Object.entries(trendMap).map(([date, v]) => ({
      date: date.slice(5), hadir: v.hadir, absen: v.absen,
    })));
  };

  useEffect(() => {
    load();
    const ch = supabase.channel("dashboard-rt")
      .on("postgres_changes", { event: "*", schema: "public", table: "students" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "teachers" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "announcements" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "attendance" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "schedules" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "gallery" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "payments" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const cards = useMemo(() => [
    { label: "Total Siswa", value: stats.students.toLocaleString("id-ID"), icon: Users, tint: "bg-primary/10 text-primary" },
    { label: "Total Guru", value: stats.teachers.toLocaleString("id-ID"), icon: GraduationCap, tint: "bg-info/10 text-info" },
    { label: "Pengumuman", value: stats.announcements.toLocaleString("id-ID"), icon: Megaphone, tint: "bg-warning/10 text-warning" },
    { label: "Absensi Bulan Ini", value: stats.attendanceMonth.toLocaleString("id-ID"), icon: ClipboardCheck, tint: "bg-primary/10 text-primary" },
    { label: "Jadwal Pelajaran", value: stats.schedules.toLocaleString("id-ID"), icon: CalendarDays, tint: "bg-info/10 text-info" },
    { label: "Galeri", value: stats.gallery.toLocaleString("id-ID"), icon: Images, tint: "bg-accent text-accent-foreground" },
  ], [stats]);

  const payCards = useMemo(() => [
    { label: "Pembayaran Hari Ini", value: new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(stats.payToday), icon: Wallet, tint: "bg-primary/10 text-primary" },
    { label: "Menunggu Verifikasi", value: stats.payPending.toLocaleString("id-ID"), icon: Clock, tint: "bg-warning/10 text-warning" },
    { label: "Pembayaran Lunas", value: stats.payPaid.toLocaleString("id-ID"), icon: CheckCircle2, tint: "bg-success/10 text-success" },
    { label: "Pembayaran Ditolak", value: stats.payRejected.toLocaleString("id-ID"), icon: Ban, tint: "bg-destructive/10 text-destructive" },
  ], [stats]);

  return (
    <div>
      <MobileHomeMenu />

      {!isMobile && (
        <div className="mt-6 md:mt-0">
          <PageHeader title="Dashboard" description="Statistik realtime sekolah." />

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {cards.map((c) => (
              <Card key={c.label} className="p-4 rounded-2xl border-border/60 hover:shadow-[var(--shadow-elevation-2)] transition-shadow">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.tint} mb-3`}>
                  <c.icon className="w-5 h-5" />
                </div>
                <div className="text-2xl font-bold">{c.value}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{c.label}</div>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-4">
            {payCards.map((c) => (
              <Card key={c.label} className="p-4 rounded-2xl border-border/60 hover:shadow-[var(--shadow-elevation-2)] transition-shadow">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.tint} mb-3`}>
                  <c.icon className="w-5 h-5" />
                </div>
                <div className="text-xl sm:text-2xl font-bold">{c.value}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{c.label}</div>
              </Card>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-4 mt-6">
            <Card className="p-5 rounded-2xl lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold">Tren Kehadiran 7 Hari</h3>
                  <p className="text-xs text-muted-foreground">Hadir vs ketidakhadiran</p>
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={attTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                    <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid var(--color-border)" }} />
                    <Line type="monotone" dataKey="hadir" stroke="var(--color-chart-1)" strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="absen" stroke="var(--color-chart-5)" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-5 rounded-2xl">
              <h3 className="font-semibold mb-1">Status Kehadiran Bulan Ini</h3>
              <p className="text-xs text-muted-foreground mb-4">Distribusi status absensi</p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={attendanceData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2}>
                      {attendanceData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid var(--color-border)" }} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-5 rounded-2xl lg:col-span-3">
              <h3 className="font-semibold mb-1">Pengumuman per Kategori</h3>
              <p className="text-xs text-muted-foreground mb-4">Total pengumuman menurut kategori</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={annByCat}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="var(--color-muted-foreground)" />
                    <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid var(--color-border)" }} />
                    <Bar dataKey="value" fill="var(--color-primary)" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
